import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Loader2, CheckCircle2, X, Clock, Calendar as CalendarIcon, MessageCircle, Pencil, AlertTriangle, User as UserIcon, Ban, Trash2, CalendarPlus, CalendarClock } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useTerm } from "@/contexts/branding";
import { googleCalendarLink } from "@/pages/IntegrationsTab";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { sendWhatsApp } from "@/lib/whatsapp";
import FileUploader from "@/components/FileUploader";
import IconAction from "@/components/IconAction";
import AgendaBlockDialog from "@/components/AgendaBlockDialog";
import ClientProfileCard from "@/components/ClientProfileCard";
import HolidaysStrip from "@/components/HolidaysStrip";
import PendingConfirmationsCard from "@/components/PendingConfirmationsCard";

const TID = {
  newAppt: "agenda-new-appt",
  newService: "agenda-new-service",
  apptForm: "agenda-appt-form",
  apptClient: "agenda-appt-client",
  apptService: "agenda-appt-service",
  apptStart: "agenda-appt-start",
  apptSubmit: "agenda-appt-submit",
  appt: (id) => `appt-${id}`,
  apptComplete: (id) => `appt-complete-${id}`,
  apptNoShow: (id) => `appt-noshow-${id}`,
  apptCancel: (id) => `appt-cancel-${id}`,
};

const STATUS_STYLES = {
  scheduled: "bg-blue-500/10 text-blue-700 border-blue-500/30",
  completed: "bg-emerald-500/10 text-emerald-700 border-emerald-500/30",
  no_show: "bg-amber-500/10 text-amber-700 border-amber-500/30",
  cancelled: "bg-zinc-500/10 text-zinc-700 border-zinc-500/30",
  date_reserved: "bg-purple-500/10 text-purple-700 border-purple-500/30",
};

const STATUS_LABEL = {
  scheduled: "Agendado", completed: "Concluído", no_show: "Não compareceu", cancelled: "Cancelado",
  date_reserved: "Data Reservada",
};

export default function TenantAgenda() {
  const auth = useAuth();
  const term = useTerm(auth?.tenant);
  const slug = auth?.tenant?.slug;
  const bookingUrl = slug ? `${window.location.origin}/p/${slug}/agendar` : null;
  const [searchParams, setSearchParams] = useSearchParams();
  // ?view=list → aba "Atendimentos" (lista). Sem query ou view=calendar → "Calendário".
  const urlView = searchParams.get("view");
  const activeTab = urlView === "list" ? "appointments" : "calendar";
  return (
    <div className="p-6 md:p-10 max-w-7xl">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.25em] text-muted-foreground">Agenda</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1">{term("appointment_plural")}</h1>
        <p className="text-sm text-muted-foreground mt-1">Organize sua agenda. O catálogo de serviços agora fica em <strong>Financeiro → Serviços</strong>.</p>
      </div>

      <Tabs value={activeTab} onValueChange={(v) => {
        const next = new URLSearchParams(searchParams);
        if (v === "appointments") next.set("view", "list");
        else next.delete("view");
        setSearchParams(next, { replace: true });
      }}>
        <TabsList>
          <TabsTrigger value="calendar">Calendário</TabsTrigger>
          <TabsTrigger value="appointments">{term("appointment_plural")}</TabsTrigger>
        </TabsList>
        <TabsContent value="calendar" className="mt-6"><WeekCalendar /></TabsContent>
        <TabsContent value="appointments" className="mt-6"><Appointments /></TabsContent>
      </Tabs>
    </div>
  );
}

function WeekCalendar() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [weekStart, setWeekStart] = useState(() => {
    const d = new Date();
    const day = d.getDay(); // 0..6 (sun..sat)
    d.setDate(d.getDate() - day);
    d.setHours(0, 0, 0, 0);
    return d;
  });

  // Janela de 7h..21h (14 horas). Cada hora = 60px.
  const START_HOUR = 7;
  const END_HOUR = 21;
  const TOTAL_HOURS = END_HOUR - START_HOUR;
  const HOUR_HEIGHT = 60; // px por hora
  const COL_HEIGHT = TOTAL_HOURS * HOUR_HEIGHT;
  const MIN_TO_PX = HOUR_HEIGHT / 60; // 1 minuto = 1px

  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + i);
    return d;
  });
  const hours = Array.from({ length: TOTAL_HOURS }, (_, i) => i + START_HOUR);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const from = days[0].toISOString().slice(0, 10);
      const last = new Date(days[6]); last.setDate(last.getDate() + 1);
      const to = last.toISOString().slice(0, 10);
      const { data } = await api.get("/tenant/appointments", { params: { from_date: from, to_date: to } });
      setItems(data.items || []);
      setLoading(false);
    })();
    // eslint-disable-next-line
  }, [weekStart.toISOString()]);

  const goPrev = () => { const d = new Date(weekStart); d.setDate(d.getDate() - 7); setWeekStart(d); };
  const goNext = () => { const d = new Date(weekStart); d.setDate(d.getDate() + 7); setWeekStart(d); };
  const goToday = () => {
    const d = new Date(); d.setDate(d.getDate() - d.getDay()); d.setHours(0, 0, 0, 0); setWeekStart(d);
  };

  const sameDay = (a, b) => a.toDateString() === b.toDateString();

  // Calcula posicionamento absoluto preciso (top/height em px) baseado no horário real.
  const positioned = (day) =>
    items
      .filter((a) => sameDay(new Date(a.start_at), day))
      .map((a) => {
        const start = new Date(a.start_at);
        const minutesFromStartOfDay = start.getHours() * 60 + start.getMinutes();
        const offsetMin = minutesFromStartOfDay - START_HOUR * 60;
        const duration = Math.max(15, a.duration_minutes || 30);
        // Clamp dentro da janela visível
        const top = Math.max(0, offsetMin * MIN_TO_PX);
        const maxHeight = Math.max(0, COL_HEIGHT - top);
        const height = Math.min(duration * MIN_TO_PX, maxHeight);
        const offscreen = offsetMin < 0 || offsetMin >= TOTAL_HOURS * 60;
        return { a, top, height, offscreen };
      })
      .filter((x) => !x.offscreen);

  return (
    <Card className="rounded-tenant border-border overflow-hidden">
      <div className="px-4 py-3 border-b border-border flex items-center justify-between gap-3">
        <div className="font-display text-lg font-semibold">
          {days[0].toLocaleDateString("pt-BR", { day: "2-digit", month: "short" })}
          {" – "}
          {days[6].toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" })}
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={goPrev} data-testid="cal-prev">‹</Button>
          <Button size="sm" variant="outline" onClick={goToday} data-testid="cal-today">Hoje</Button>
          <Button size="sm" variant="outline" onClick={goNext} data-testid="cal-next">›</Button>
        </div>
      </div>
      {loading ? <div className="p-10 text-center text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin inline" /></div> :
        <div className="overflow-x-auto">
          <div className="grid grid-cols-[60px_repeat(7,minmax(120px,1fr))] min-w-[860px] text-xs">
            {/* Header row: empty + 7 dias */}
            <div className="border-b border-r border-border bg-muted/30" />
            {days.map((d, i) => {
              const isToday = sameDay(d, new Date());
              return (
                <div key={i} className={`border-b border-r border-border px-2 py-2 text-center ${isToday ? "bg-tenant/5 font-semibold text-tenant" : "bg-muted/30"}`}>
                  <div className="uppercase text-[10px] tracking-wider">{d.toLocaleDateString("pt-BR", { weekday: "short" })}</div>
                  <div className="text-lg font-display font-semibold mt-0.5">{d.getDate()}</div>
                </div>
              );
            })}

            {/* Coluna de horas (esquerda) */}
            <div className="border-r border-border relative" style={{ height: COL_HEIGHT }}>
              {hours.map((h, i) => (
                <div
                  key={h}
                  className="absolute left-0 right-0 border-b border-border px-2 text-muted-foreground font-mono text-[10px]"
                  style={{ top: i * HOUR_HEIGHT, height: HOUR_HEIGHT }}
                >
                  <span className="block pt-1">{String(h).padStart(2, "0")}:00</span>
                </div>
              ))}
            </div>

            {/* 7 colunas de dia (com blocos posicionados absolutamente) */}
            {days.map((d, di) => {
              const blocks = positioned(d);
              const isToday = sameDay(d, new Date());
              // Indicador do "agora" na coluna de hoje
              let nowTop = null;
              if (isToday) {
                const now = new Date();
                const m = now.getHours() * 60 + now.getMinutes() - START_HOUR * 60;
                if (m >= 0 && m <= TOTAL_HOURS * 60) nowTop = m * MIN_TO_PX;
              }
              return (
                <div
                  key={`col-${di}`}
                  className="border-r border-border relative"
                  style={{ height: COL_HEIGHT }}
                  data-testid={`cal-col-${di}`}
                >
                  {/* Linhas guia (uma por hora) */}
                  {hours.map((_, hi) => (
                    <div
                      key={hi}
                      className="absolute left-0 right-0 border-b border-border/60"
                      style={{ top: hi * HOUR_HEIGHT, height: HOUR_HEIGHT }}
                    />
                  ))}
                  {/* Linha do "agora" */}
                  {nowTop !== null && (
                    <div
                      className="absolute left-0 right-0 z-10 pointer-events-none"
                      style={{ top: nowTop }}
                      data-testid="cal-now-line"
                    >
                      <div className="h-[2px] bg-red-500" />
                    </div>
                  )}
                  {/* Blocos de agendamentos */}
                  {blocks.map(({ a, top, height }) => {
                    const isCancelled = a.status === "cancelled";
                    const isNoShow = a.status === "no_show";
                    const isCompleted = a.status === "completed";
                    const isPublic = a.source === "public_booking";
                    const bg = isCancelled
                      ? "rgba(120,120,120,0.10)"
                      : isNoShow
                      ? "rgba(245,158,11,0.16)"
                      : isCompleted
                      ? "rgba(16,185,129,0.16)"
                      : isPublic
                      ? "rgba(20,184,166,0.18)" /* teal para auto-agendado */
                      : "hsl(var(--tenant-primary) / 0.16)";
                    const border = isCancelled
                      ? "rgba(120,120,120,0.6)"
                      : isNoShow
                      ? "rgb(245,158,11)"
                      : isCompleted
                      ? "rgb(16,185,129)"
                      : isPublic
                      ? "rgb(20,184,166)"
                      : "hsl(var(--tenant-primary))";
                    const startStr = new Date(a.start_at).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
                    const endDate = new Date(new Date(a.start_at).getTime() + (a.duration_minutes || 30) * 60000);
                    const endStr = endDate.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
                    return (
                      <div
                        key={a.id}
                        title={`${startStr}–${endStr} · ${a.client_name} · ${a.service_name}${isPublic ? " · Auto-agendamento" : ""}`}
                        data-testid={`cal-block-${a.id}`}
                        className={`absolute left-1 right-1 rounded-md px-1.5 py-1 text-[11px] leading-tight overflow-hidden shadow-sm ${isCancelled ? "line-through opacity-70" : ""}`}
                        style={{
                          top,
                          height: Math.max(18, height - 2),
                          background: bg,
                          borderLeft: `3px solid ${border}`,
                        }}
                      >
                        <div className="font-semibold truncate flex items-center gap-1">
                          {isPublic && <span className="text-[8px] uppercase tracking-wider px-1 rounded bg-teal-700 text-white">Online</span>}
                          {startStr}–{endStr} · {a.client_name}
                        </div>
                        {height > 28 && (
                          <div className="text-muted-foreground truncate">{a.service_name}</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>}
    </Card>
  );
}


function ReserveDateDialog({ open, onOpenChange, clients, services, onSaved }) {
  const [form, setForm] = useState({ client_id: "", service_id: "", reserved_date: "", notes: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) setForm({ client_id: "", service_id: "", reserved_date: "", notes: "" });
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.client_id || !form.service_id || !form.reserved_date) {
      toast.error("Preencha cliente, serviço e data");
      return;
    }
    setBusy(true);
    try {
      await api.post("/tenant/appointments/reserve", form);
      toast.success("Data reservada!");
      onSaved?.();
      onOpenChange(false);
    } catch (err) { toast.error(formatApiError(err, "Erro ao reservar data")); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="reserve-date-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Reservar Data</DialogTitle>
          <DialogDescription>
            Registre cliente e data. O horário fica em aberto até você definir depois.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label>Cliente</Label>
            <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
              <SelectTrigger data-testid="reserve-client"><SelectValue placeholder="Selecione" /></SelectTrigger>
              <SelectContent>{clients.map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Serviço</Label>
            <Select value={form.service_id} onValueChange={(v) => setForm({ ...form, service_id: v })}>
              <SelectTrigger data-testid="reserve-service"><SelectValue placeholder="Selecione" /></SelectTrigger>
              <SelectContent>{services.map((s) => (
                <SelectItem key={s.id} value={s.id}>{s.name} ({s.duration_minutes}min)</SelectItem>
              ))}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Data reservada</Label>
            <Input type="date" required data-testid="reserve-date"
                   value={form.reserved_date}
                   onChange={(e) => setForm({ ...form, reserved_date: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label>Observações (opcional)</Label>
            <Input placeholder="Ex: período preferido, pacote contratado..."
                   value={form.notes}
                   onChange={(e) => setForm({ ...form, notes: e.target.value })}
                   data-testid="reserve-notes" />
          </div>
          <DialogFooter>
            <Button variant="ghost" type="button" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" disabled={busy} data-testid="reserve-submit"
                    style={{ background: "hsl(var(--tenant-primary))", color: "hsl(var(--tenant-primary-foreground))" }}>
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Reservar data"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}


function AssignTimeDialog({ open, onOpenChange, appt, onSaved }) {
  const [startAt, setStartAt] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open && appt) {
      // Pré-preenche com a data reservada + 09:00
      const d = new Date(appt.start_at);
      const pad = (n) => String(n).padStart(2, "0");
      setStartAt(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T09:00`);
    }
  }, [open, appt]);

  const submit = async (e) => {
    e.preventDefault();
    if (!startAt) return;
    setBusy(true);
    try {
      await api.post(`/tenant/appointments/${appt.id}/assign-time`, { start_at: new Date(startAt).toISOString() });
      toast.success("Horário atribuído — status: Agendado");
      onSaved?.();
      onOpenChange(false);
    } catch (err) { toast.error(formatApiError(err, "Erro ao atribuir horário")); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid="assign-time-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Atribuir horário</DialogTitle>
          <DialogDescription>
            {appt?.client_name} — {appt?.service_name}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label>Data e horário</Label>
            <Input type="datetime-local" required data-testid="assign-time-start"
                   value={startAt}
                   onChange={(e) => setStartAt(e.target.value)} />
          </div>
          <DialogFooter>
            <Button variant="ghost" type="button" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" disabled={busy} data-testid="assign-time-submit"
                    style={{ background: "hsl(var(--tenant-primary))", color: "hsl(var(--tenant-primary-foreground))" }}>
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirmar agendamento"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}


function AppointmentFormDialog({ open, onOpenChange, initial, clients, services, resources, onSaved }) {
  // initial=null → create; initial=obj → edit
  const isEdit = !!initial;
  const [form, setForm] = useState({ client_id: "", service_id: "", start_at: "", notes: "", resource_id: "", resource_ids: [], client_package_id: "", subscription_id: "" });
  const [busy, setBusy] = useState(false);
  const [conflict, setConflict] = useState(null); // { conflicts: [...], pendingPayload }
  const [clientPackages, setClientPackages] = useState([]);
  const [clientSubs, setClientSubs] = useState([]);

  useEffect(() => {
    if (open) {
      if (initial) {
        // Converte ISO (com tz) para datetime-local: YYYY-MM-DDTHH:mm
        const dt = new Date(initial.start_at);
        const pad = (n) => String(n).padStart(2, "0");
        const localISO = `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
        // Compat: se vier `resource_ids` do backend usa ele; senão cai no antigo `resource_id`.
        const initialIds = Array.isArray(initial.resource_ids) && initial.resource_ids.length > 0
          ? initial.resource_ids
          : (initial.resource_id ? [initial.resource_id] : []);
        setForm({
          client_id: initial.client_id || "",
          service_id: initial.service_id || "",
          start_at: localISO,
          notes: initial.notes || "",
          resource_id: initialIds[0] || "",
          resource_ids: initialIds,
          client_package_id: initial.client_package_id || "",
          subscription_id: initial.subscription_id || "",
        });
      } else {
        setForm({ client_id: "", service_id: "", start_at: "", notes: "", resource_id: "", resource_ids: [], client_package_id: "", subscription_id: "" });
      }
      setConflict(null);
      setClientPackages([]);
      setClientSubs([]);
    }
  }, [open, initial]);

  // Ao trocar de cliente, busca pacotes ativos (com saldo) E assinaturas ativas do cliente.
  useEffect(() => {
    if (!form.client_id) { setClientPackages([]); setClientSubs([]); return; }
    let cancelled = false;
    Promise.all([
      api.get("/tenant/financeiro/client-packages", { params: { client_id: form.client_id, only_active: true } }),
      api.get("/tenant/financeiro/subscriptions", { params: { client_id: form.client_id, status: "active" } }),
    ]).then(([pkgRes, subRes]) => {
      if (cancelled) return;
      setClientPackages(pkgRes.data.items || []);
      setClientSubs(subRes.data.items || []);
    }).catch(() => { if (!cancelled) { setClientPackages([]); setClientSubs([]); } });
    return () => { cancelled = true; };
  }, [form.client_id]);

  // Se o pacote selecionado não bate mais com o serviço escolhido, limpa.
  useEffect(() => {
    if (!form.client_package_id) return;
    const cp = clientPackages.find((p) => p.id === form.client_package_id);
    if (!cp) return;
    if (form.service_id && cp.service_id && cp.service_id !== form.service_id) {
      setForm((f) => ({ ...f, client_package_id: "" }));
    }
  }, [form.service_id, form.client_package_id, clientPackages]);

  const applicablePackages = form.service_id
    ? clientPackages.filter((cp) => !cp.service_id || cp.service_id === form.service_id)
    : clientPackages;
  const hasCoverage = applicablePackages.length > 0 || clientSubs.length > 0;

  const buildPayload = () => {
    const p = { ...form };
    // Multi-professional: envia resource_ids se >0, senão null (qualquer profissional)
    if (p.resource_ids && p.resource_ids.length > 0) {
      p.resource_id = p.resource_ids[0]; // compat retroativa
    } else {
      p.resource_id = null;
      p.resource_ids = [];
    }
    if (!p.notes) p.notes = "";
    if (!p.client_package_id) p.client_package_id = null;
    if (!p.subscription_id) p.subscription_id = null;
    return p;
  };

  const doSave = async (force = false) => {
    setBusy(true);
    try {
      const payload = buildPayload();
      const url = isEdit
        ? `/tenant/appointments/${initial.id}${force ? "?force=true" : ""}`
        : `/tenant/appointments${force ? "?force=true" : ""}`;
      const method = isEdit ? "put" : "post";
      await api[method](url, payload);
      toast.success(isEdit ? "Agendamento atualizado" : "Agendamento criado");
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && detail?.code === "schedule_conflict") {
        setConflict({ conflicts: detail.conflicts || [], pendingPayload: buildPayload() });
      } else if (err?.response?.status === 409 && detail?.code === "agenda_blocked") {
        toast.error(detail?.message || "Horário bloqueado");
      } else {
        toast.error(formatApiError(err));
      }
    } finally {
      setBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    await doSave(false);
  };

  const confirmConflict = async () => {
    await doSave(true);
    setConflict(null);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-display">{isEdit ? "Editar agendamento" : "Novo agendamento"}</DialogTitle>
          <DialogDescription>Selecione cliente, serviço, profissional e horário.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid={TID.apptForm}>
          <div className="space-y-1.5">
            <Label>Cliente</Label>
            <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
              <SelectTrigger data-testid={TID.apptClient}><SelectValue placeholder="Selecione" /></SelectTrigger>
              <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          {/* Perfil individualizado + slots sugeridos ao selecionar o cliente */}
          {!isEdit && form.client_id && (
            <ClientProfileCard
              clientId={form.client_id}
              onPickSlot={(s) => {
                // Preenche o form com o slot escolhido; ajusta serviço/profissional se vieram sugeridos
                const dt = new Date(s.start_at);
                const pad = (n) => String(n).padStart(2, "0");
                const local = `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
                setForm((prev) => {
                  const nextIds = s.professional_id
                    ? (prev.resource_ids.includes(s.professional_id) ? prev.resource_ids : [s.professional_id])
                    : prev.resource_ids;
                  return {
                    ...prev,
                    start_at: local,
                    service_id: s.service_id || prev.service_id,
                    resource_id: nextIds[0] || "",
                    resource_ids: nextIds,
                  };
                });
              }}
            />
          )}
          <div className="space-y-1.5">
            <Label>Serviço</Label>
            <Select value={form.service_id} onValueChange={(v) => setForm({ ...form, service_id: v })}>
              <SelectTrigger data-testid={TID.apptService}><SelectValue placeholder="Selecione" /></SelectTrigger>
              <SelectContent>{services.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} · {s.duration_minutes}min</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Profissional(is) <span className="text-muted-foreground text-xs">(opcional · pode selecionar mais de um)</span></Label>
            {form.resource_ids.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-2" data-testid="agenda-appt-resource-chips">
                {form.resource_ids.map((rid) => {
                  const r = resources.find((x) => x.id === rid);
                  if (!r) return null;
                  return (
                    <span key={rid}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs bg-tenant/10 border border-tenant/20"
                      data-testid={`agenda-appt-resource-chip-${rid}`}>
                      {r.name}
                      <button type="button"
                        className="hover:text-destructive"
                        aria-label={`Remover ${r.name}`}
                        onClick={() => setForm({ ...form, resource_ids: form.resource_ids.filter((x) => x !== rid) })}
                        data-testid={`agenda-appt-resource-remove-${rid}`}>
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  );
                })}
              </div>
            )}
            <Select
              value=""
              onValueChange={(v) => {
                if (v && v !== "_none" && !form.resource_ids.includes(v)) {
                  setForm({ ...form, resource_ids: [...form.resource_ids, v] });
                }
              }}
            >
              <SelectTrigger data-testid="agenda-appt-resource">
                <SelectValue placeholder={form.resource_ids.length === 0 ? "Qualquer profissional" : "Adicionar outro profissional…"} />
              </SelectTrigger>
              <SelectContent>
                {resources.filter((r) => !form.resource_ids.includes(r.id)).length === 0 ? (
                  <SelectItem value="_none" disabled>Todos já adicionados</SelectItem>
                ) : (
                  resources
                    .filter((r) => !form.resource_ids.includes(r.id))
                    .map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)
                )}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              {form.resource_ids.length > 1
                ? `${form.resource_ids.length} profissionais atenderão em paralelo. Conflitos serão checados em todos.`
                : "Definir profissional(is) ativa a detecção de conflito de horário."}
            </p>
          </div>
          <div className="space-y-1.5">
            <Label>Início</Label>
            <Input required type="datetime-local" data-testid={TID.apptStart}
              value={form.start_at}
              onChange={(e) => setForm({ ...form, start_at: e.target.value })} />
          </div>

          {/* Cobertura por pacote pré-pago OU assinatura ativa (mutex) */}
          {hasCoverage && (
            <div
              className="p-3 rounded-lg border border-tenant/40 space-y-2"
              data-testid="appt-package-picker"
              style={{ background: "hsl(var(--tenant-primary) / 0.05)" }}
            >
              <div className="min-w-0">
                <div className="text-sm font-medium">Este atendimento é coberto por algum benefício?</div>
                <div className="text-[11px] text-muted-foreground">
                  {applicablePackages.length > 0 && `${applicablePackages.length} pacote(s) com saldo`}
                  {applicablePackages.length > 0 && clientSubs.length > 0 && " · "}
                  {clientSubs.length > 0 && `${clientSubs.length} assinatura(s) ativa(s)`}
                </div>
              </div>
              <div className="space-y-1.5">
                <label className="flex items-center gap-2 p-2 rounded-md hover:bg-muted/40 cursor-pointer">
                  <input
                    type="radio"
                    name="appt-coverage"
                    checked={!form.client_package_id && !form.subscription_id}
                    onChange={() => setForm({ ...form, client_package_id: "", subscription_id: "" })}
                    data-testid="appt-package-none"
                  />
                  <span className="text-sm">Cobrar normalmente (sem benefício)</span>
                </label>
                {applicablePackages.map((cp) => {
                  const remaining = cp.sessions_total - cp.sessions_used;
                  return (
                    <label
                      key={cp.id}
                      className="flex items-center gap-2 p-2 rounded-md hover:bg-muted/40 cursor-pointer"
                      data-testid={`appt-package-opt-${cp.id}`}
                    >
                      <input
                        type="radio"
                        name="appt-coverage"
                        checked={form.client_package_id === cp.id}
                        onChange={() => setForm({ ...form, client_package_id: cp.id, subscription_id: "" })}
                      />
                      <span className="text-sm flex-1 min-w-0 truncate">
                        📦 Descontar saldo do Pacote <b>{cp.package_name}</b>
                      </span>
                      <span className="text-[11px] text-muted-foreground shrink-0">
                        {remaining} de {cp.sessions_total} restantes
                      </span>
                    </label>
                  );
                })}
                {clientSubs.map((sub) => (
                  <label
                    key={sub.id}
                    className="flex items-center gap-2 p-2 rounded-md hover:bg-muted/40 cursor-pointer"
                    data-testid={`appt-subscription-opt-${sub.id}`}
                  >
                    <input
                      type="radio"
                      name="appt-coverage"
                      checked={form.subscription_id === sub.id}
                      onChange={() => setForm({ ...form, subscription_id: sub.id, client_package_id: "" })}
                    />
                    <span className="text-sm flex-1 min-w-0 truncate">
                      🔁 Cobrir pelo plano <b>{sub.plan_name}</b>
                    </span>
                    <span className="text-[11px] text-muted-foreground shrink-0">
                      R$ {Number(sub.monthly_price || 0).toFixed(2)}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button type="submit" data-testid={TID.apptSubmit} disabled={busy || !form.client_id || !form.service_id || !form.start_at} className="gap-2"
              style={{ background: `hsl(var(--tenant-primary))`, color: `hsl(var(--tenant-primary-foreground))` }}>
              {busy && <Loader2 className="w-4 h-4 animate-spin" />} {isEdit ? "Salvar" : "Agendar"}
            </Button>
          </DialogFooter>
        </form>

        {/* Sub-modal interno: confirmação de conflito */}
        <Dialog open={!!conflict} onOpenChange={(o) => !o && setConflict(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-display text-amber-700 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" /> Conflito de horário
              </DialogTitle>
              <DialogDescription>
                Este profissional já tem agendamento(s) nesse horário. Confirmar mesmo assim significa que ele(a) fará os atendimentos em paralelo.
              </DialogDescription>
            </DialogHeader>
            <ul className="space-y-2 text-sm" data-testid="conflict-list">
              {(conflict?.conflicts || []).map((c) => (
                <li key={c.id} className="p-3 rounded-md border border-amber-200 bg-amber-50 dark:bg-amber-900/20">
                  <div className="font-medium">{c.client_name}</div>
                  <div className="text-xs text-muted-foreground">
                    {c.service_name} · {new Date(c.start_at).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })}
                    {" · "}{c.duration_minutes}min
                  </div>
                </li>
              ))}
            </ul>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setConflict(null)} data-testid="conflict-cancel">Cancelar</Button>
              <Button onClick={confirmConflict} disabled={busy} data-testid="conflict-confirm" className="bg-amber-600 hover:bg-amber-700 text-white">
                {busy && <Loader2 className="w-4 h-4 animate-spin mr-2" />} Confirmar (faço os dois)
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}

const WEEKDAY_SHORT = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];
function BlocksList({ blocks, onDelete }) {
  return (
    <Card className="rounded-tenant border-red-500/20 bg-red-500/5 p-4 space-y-2" data-testid="agenda-blocks-list">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-red-700 font-semibold">
        <Ban className="w-3.5 h-3.5" /> Bloqueios ativos
      </div>
      <ul className="divide-y divide-red-500/10">
        {blocks.map((b) => {
          const scopeLabel = b.scope === "tenant" ? "Empresa toda" : (b.professional_name || "Profissional");
          const summary = b.type === "one_off"
            ? `${new Date(b.start_at).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" })} · ${b.start_at.slice(11, 16)}–${b.end_at.slice(11, 16)}`
            : `${(b.weekdays || []).map((w) => WEEKDAY_SHORT[w]).join(", ")} · ${b.start_time}–${b.end_time}${b.starts_on || b.ends_on ? ` (${b.starts_on || "…"} → ${b.ends_on || "sem fim"})` : ""}`;
          return (
            <li key={b.id} className="py-1.5 flex items-center gap-2 justify-between" data-testid={`agenda-block-${b.id}`}>
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <Badge variant="outline" className="text-[10px] bg-red-500/10 text-red-700 border-red-500/40 shrink-0">
                  {b.type === "one_off" ? "Pontual" : "Recorrente"}
                </Badge>
                <div className="min-w-0">
                  <div className="text-xs font-medium truncate">{scopeLabel} · {summary}</div>
                  {b.reason && <div className="text-[10px] text-muted-foreground truncate">{b.reason}</div>}
                </div>
              </div>
              <Button size="sm" variant="ghost" onClick={() => onDelete(b.id)}
                data-testid={`agenda-block-delete-${b.id}`}
                className="h-7 w-7 p-0 text-muted-foreground hover:text-red-700 hover:bg-red-500/10 shrink-0">
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

function Appointments() {
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);
  const [resources, setResources] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [blockOpen, setBlockOpen] = useState(false);
  const [reserveOpen, setReserveOpen] = useState(false);
  const [assigningAppt, setAssigningAppt] = useState(null);
  const [editing, setEditing] = useState(null);  // appointment being edited (null = create mode)
  const [completing, setCompleting] = useState(null);  // appointment being completed
  const [npsInvite, setNpsInvite] = useState(null); // { token, url, phone, name } after completing

  const load = async () => {
    setLoading(true);
    try {
      const [a, c, s, r, b] = await Promise.all([
        api.get("/tenant/appointments"),
        api.get("/tenant/clients", { params: { limit: 500 } }),
        api.get("/tenant/services"),
        api.get("/tenant/resources"),
        api.get("/tenant/agenda/blocks"),
      ]);
      setItems(a.data.items || []);
      setClients(c.data.items || []);
      setServices(s.data.items || []);
      setResources(r.data.items || []);
      setBlocks(b.data.raw || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const deleteBlock = async (bid) => {
    try {
      await api.delete(`/tenant/agenda/blocks/${bid}`);
      toast.success("Bloqueio removido");
      load();
    } catch (er) { toast.error(formatApiError(er)); }
  };

  const groupedByDate = useMemo(() => {
    // Ordena todos os agendamentos por start_at desc (mais recentes primeiro),
    // e mantém a ordem de grupos preservada (Map + insertion order).
    const sorted = [...items].sort((a, b) => new Date(b.start_at) - new Date(a.start_at));
    const groups = new Map();
    for (const a of sorted) {
      const key = new Date(a.start_at).toLocaleDateString("pt-BR", { weekday: "short", day: "2-digit", month: "short" });
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(a);
    }
    return Object.fromEntries(groups);
  }, [items]);

  const setStatus = async (id, status) => {
    try {
      await api.put(`/tenant/appointments/${id}`, { status });
      toast.success(`Status atualizado: ${STATUS_LABEL[status]}`);
      load();
    } catch (er) { toast.error(formatApiError(er)); }
  };

  const startEdit = (a) => { setEditing(a); setDialogOpen(true); };
  const startCreate = () => { setEditing(null); setDialogOpen(true); };

  if (loading) return <div className="text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <div className="text-sm text-muted-foreground">{items.length} agendamentos</div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" className="gap-2" onClick={() => setBlockOpen(true)}
            data-testid="agenda-block-btn">
            <Ban className="w-4 h-4 text-red-600" /> Bloquear horário
          </Button>
          <Button variant="outline" className="gap-2" onClick={() => setReserveOpen(true)}
            data-testid="agenda-reserve-date-btn"
            disabled={clients.length === 0 || services.length === 0}>
            <CalendarPlus className="w-4 h-4 text-purple-600" /> Reservar Data
          </Button>
          <Button data-testid={TID.newAppt} className="gap-2" onClick={startCreate}
            style={{ background: `hsl(var(--tenant-primary))`, color: `hsl(var(--tenant-primary-foreground))` }}
            disabled={clients.length === 0 || services.length === 0}>
            <Plus className="w-4 h-4" /> Novo agendamento
          </Button>
        </div>
      </div>

      <ReserveDateDialog
        open={reserveOpen}
        onOpenChange={setReserveOpen}
        clients={clients}
        services={services}
        onSaved={load}
      />
      <AssignTimeDialog
        open={!!assigningAppt}
        onOpenChange={(v) => { if (!v) setAssigningAppt(null); }}
        appt={assigningAppt}
        onSaved={load}
      />

      <AppointmentFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        initial={editing}
        clients={clients}
        services={services}
        resources={resources}
        onSaved={load}
      />

      <AgendaBlockDialog
        open={blockOpen}
        onOpenChange={setBlockOpen}
        resources={resources}
        onSaved={load}
      />

      <NpsInviteDialog
        invite={npsInvite}
        onClose={() => setNpsInvite(null)}
      />

      {blocks.length > 0 && <BlocksList blocks={blocks} onDelete={deleteBlock} />}

      <PendingConfirmationsCard tenantName={undefined} />

      <HolidaysStrip />

      {Object.keys(groupedByDate).length === 0 ? (
        <Card className="p-12 text-center text-muted-foreground rounded-tenant">Sem agendamentos. {(clients.length === 0 || services.length === 0) && "Cadastre clientes e serviços primeiro."}</Card>
      ) : Object.entries(groupedByDate).map(([d, list]) => (
        <div key={d}>
          <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2">{d}</div>
          <Card className="rounded-tenant border-border overflow-hidden">
            <ul className="divide-y divide-border">
              {list.map((a) => (
                <li key={a.id} data-testid={TID.appt(a.id)} className="px-5 py-3 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="text-sm font-mono font-semibold">
                      {a.status === "date_reserved"
                        ? <span className="text-purple-700 text-[11px]">a definir</span>
                        : new Date(a.start_at).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
                    </div>
                    <div className="min-w-0">
                      <div className="font-medium truncate flex items-center gap-2">
                        {a.client_name}
                        {a.source === "public_booking" && (
                          <span data-testid={`appt-source-public-${a.id}`}
                            title="Agendado pelo cliente via link público"
                            className="text-[9px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded bg-teal-500/15 text-teal-700 border border-teal-500/30">
                            Online
                          </span>
                        )}
                        {a.confirmed_by_client && (
                          <span title="Cliente confirmou presença"
                            className="text-[9px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-700 border border-emerald-500/30">
                            ✓ Confirmado
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground truncate">
                        {a.service_name} · {a.duration_minutes}min · R$ {a.price.toFixed(2)}
                        {(() => {
                          // Nomes dos profissionais (multi-professional)
                          const names = (a.resource_names && a.resource_names.length > 0)
                            ? a.resource_names
                            : (a.resource_name ? [a.resource_name] : []);
                          return names.length > 0 ? ` · ${names.join(" + ")}` : "";
                        })()}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Badge variant="outline" className={STATUS_STYLES[a.status]}>{STATUS_LABEL[a.status]}</Badge>
                    <a href={googleCalendarLink(a)} target="_blank" rel="noopener noreferrer"
                      title="Adicionar ao Google Agenda"
                      aria-label="Adicionar ao Google Agenda"
                      className="inline-flex items-center justify-center h-8 w-8 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors">
                      <CalendarIcon className="w-4 h-4" />
                    </a>
                    {a.status === "scheduled" && (
                      <>
                        <IconAction icon={Pencil} label="Editar agendamento" testid={`appt-edit-${a.id}`} onClick={() => startEdit(a)} />
                        <IconAction icon={CheckCircle2} label="Marcar como concluído" testid={TID.apptComplete(a.id)} className="text-emerald-700 hover:text-emerald-800" onClick={() => setCompleting(a)} />
                        <IconAction icon={Clock} label="Marcar como não compareceu" testid={TID.apptNoShow(a.id)} className="text-amber-700 hover:text-amber-800" onClick={() => setStatus(a.id, "no_show")} />
                        <IconAction icon={X} label="Cancelar agendamento" testid={TID.apptCancel(a.id)} className="text-destructive hover:text-destructive" onClick={() => setStatus(a.id, "cancelled")} />
                      </>
                    )}
                    {a.status === "date_reserved" && (
                      <>
                        <IconAction icon={CalendarClock} label="Atribuir horário"
                                    testid={`appt-assign-time-${a.id}`}
                                    className="text-purple-700 hover:text-purple-800"
                                    onClick={() => setAssigningAppt(a)} />
                        <IconAction icon={X} label="Cancelar reserva"
                                    testid={`appt-cancel-reserve-${a.id}`}
                                    className="text-destructive hover:text-destructive"
                                    onClick={() => setStatus(a.id, "cancelled")} />
                      </>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </Card>
        </div>
      ))}

      <CompleteAppointmentDialog
        appointment={completing}
        onClose={() => { setCompleting(null); load(); }}
        onCompleted={(nps) => {
          // ALWAYS close the dialog after completion. If backend returned an
          // NPS token, open a separate lightweight invite dialog so the admin
          // clearly sees the appointment IS concluded and the pesquisa is optional.
          setCompleting(null);
          if (nps) setNpsInvite(nps);
          load();
        }}
        onStatusUpdated={load}
      />
    </div>
  );
}

// Catálogo de serviços movido para /t/{slug}/financeiro (iter_27).
// Ver /app/frontend/src/pages/ServicesCatalog.jsx.

// ---------- Sprint 1: registro de produtos + fotos ao concluir atendimento ----------
function CompleteAppointmentDialog({ appointment, onClose, onCompleted, onStatusUpdated }) {
  const [products, setProducts] = useState("");
  const [photos, setPhotos] = useState([]);
  const [finalPrice, setFinalPrice] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("dinheiro");
  const [discountReason, setDiscountReason] = useState("");
  const [busy, setBusy] = useState(false);

  const basePrice = Number(appointment?.price || 0);
  const finalPriceNum = Number(finalPrice || 0);
  const isFreeMethod = paymentMethod === "brinde" || paymentMethod === "cortesia";
  const hasDiscount = !isFreeMethod && finalPriceNum + 0.01 < basePrice;

  useEffect(() => {
    if (appointment) {
      setProducts((appointment.products_used || []).join(", "));
      setPhotos(appointment.photos || []);
      setFinalPrice(String(appointment.price ?? ""));
      setPaymentMethod("dinheiro");
      setDiscountReason("");
    }
  }, [appointment]);

  // Ao selecionar brinde/cortesia, zera automaticamente
  useEffect(() => {
    if (isFreeMethod) setFinalPrice("0");
  }, [isFreeMethod]);

  if (!appointment) return null;

  const finish = async (savePayload, { allowBeforeStart = false } = {}) => {
    setBusy(true);
    try {
      const { data } = await api.put(
        `/tenant/appointments/${appointment.id}` +
          (allowBeforeStart ? `?allow_complete_before_start=true` : ``),
        {
          status: "completed",
          ...(savePayload || {}),
        },
      );
      toast.success(savePayload ? "Atendimento concluído e registrado" : "Atendimento concluído");
      onStatusUpdated?.();
      const nps = data?.nps_token
        ? (() => {
            const slug = window.location.pathname.split("/")[2];
            return {
              token: data.nps_token,
              url: `${window.location.origin}/nps/${slug}/${data.nps_token}`,
              phone: data.nps_client_phone || appointment.client_phone || "",
              name: data.nps_client_name || appointment.client_name || "",
            };
          })()
        : null;
      onCompleted?.(nps);
    } catch (e) {
      // Item 6 — Concluir antes do horário marcado pede confirmação.
      const code = e?.response?.data?.detail?.code;
      if (code === "complete_before_start_requires_confirm") {
        const ok = window.confirm(
          "Este atendimento ainda não começou. Concluir mesmo assim?\n\n" +
            "A duração real não será registrada (para não distorcer as " +
            "sugestões de duração).",
        );
        if (ok) {
          setBusy(false);
          return finish(savePayload, { allowBeforeStart: true });
        }
      } else {
        toast.error(formatApiError(e));
      }
    } finally {
      setBusy(false);
    }
  };

  const skip = () => finish(null);
  const saveAndComplete = () => {
    if (hasDiscount && !discountReason.trim()) {
      toast.error("Informe o motivo do desconto para concluir.");
      return;
    }
    const products_used = products.split(",").map((p) => p.trim()).filter(Boolean);
    finish({
      products_used,
      photos,
      final_price: isFreeMethod ? 0 : finalPriceNum,
      payment_method: paymentMethod,
      discount_reason: (hasDiscount || isFreeMethod) ? discountReason.trim() : "",
    });
  };

  return (
    <Dialog open={!!appointment} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-lg" data-testid="complete-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Concluir atendimento</DialogTitle>
        </DialogHeader>
        <div className="space-y-1 text-sm mb-4">
          <div className="font-medium">{appointment.client_name}</div>
          <div className="text-muted-foreground">{appointment.service_name} · {new Date(appointment.start_at).toLocaleString("pt-BR")}</div>
          <div className="text-xs text-muted-foreground">Valor original: <strong>R$ {basePrice.toFixed(2)}</strong></div>
        </div>
        <div className="space-y-3">
          {/* Fechamento financeiro */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Valor cobrado (R$)</Label>
              <Input
                type="number" step="0.01" min="0"
                value={finalPrice}
                disabled={isFreeMethod}
                onChange={(e) => setFinalPrice(e.target.value)}
                data-testid="complete-final-price"
              />
              {hasDiscount && (
                <p className="text-xs text-amber-700">
                  Desconto de R$ {(basePrice - finalPriceNum).toFixed(2)} — motivo obrigatório
                </p>
              )}
              {isFreeMethod && (
                <p className="text-xs text-muted-foreground">
                  Valor zerado automaticamente ({paymentMethod === "brinde" ? "brinde" : "cortesia"})
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label>Forma de entrada</Label>
              <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                <SelectTrigger data-testid="complete-payment-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="dinheiro">Dinheiro</SelectItem>
                  <SelectItem value="pix">PIX</SelectItem>
                  <SelectItem value="cartao">Cartão</SelectItem>
                  <SelectItem value="brinde">Brinde</SelectItem>
                  <SelectItem value="cortesia">Cortesia</SelectItem>
                  <SelectItem value="permuta">Permuta</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {(hasDiscount || isFreeMethod) && (
            <div className="space-y-1.5">
              <Label>
                Motivo {hasDiscount ? "do desconto" : (paymentMethod === "brinde" ? "do brinde" : "da cortesia")}
                {hasDiscount && <span className="text-destructive"> *</span>}
              </Label>
              <Input
                value={discountReason}
                onChange={(e) => setDiscountReason(e.target.value)}
                data-testid="complete-discount-reason"
                placeholder={
                  hasDiscount
                    ? "Ex: cliente fiel · promoção do mês · combo com outro serviço"
                    : "Ex: primeira visita · fidelidade · sorteio"
                }
                maxLength={500}
              />
            </div>
          )}
          <div className="space-y-1.5">
            <Label>Produtos utilizados (separados por vírgula)</Label>
            <Input
              data-testid="complete-products"
              value={products}
              onChange={(e) => setProducts(e.target.value)}
              placeholder="Ex.: Shampoo Inoar, Tonalizante Wella 6.0, Óleo de argan"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Fotos do atendimento (JPEG — até 10)</Label>
            <FileUploader
              testid="complete-photos"
              value={photos}
              onChange={setPhotos}
              multi
              accept="image/jpeg"
              max={10}
            />
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button type="button" variant="ghost" onClick={skip} disabled={busy} data-testid="complete-skip">
            Pular registro
          </Button>
          <Button
            type="button" onClick={saveAndComplete} disabled={busy} data-testid="complete-submit"
            className="gap-2"
            style={{ background: `hsl(var(--tenant-primary))`, color: `hsl(var(--tenant-primary-foreground))` }}
          >
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Salvar e concluir
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/**
 * NpsInviteDialog — modal secundário e OPCIONAL exibido após concluir um
 * atendimento, oferecendo o envio da pesquisa de satisfação via WhatsApp.
 *
 * Fica separado do CompleteAppointmentDialog para deixar 100% claro pro admin
 * que a conclusão JÁ aconteceu — este passo é só um "extra".
 */
function NpsInviteDialog({ invite, onClose }) {
  const send = async () => {
    if (!invite) return;
    const msg =
      `Olá ${(invite.name || "").split(" ")[0] || ""}! 😊\n\n` +
      `Obrigado por vir hoje! Você poderia avaliar seu atendimento (0-10) em 30s?\n\n${invite.url}\n\nSua opinião é super importante pra gente!`;
    await sendWhatsApp({ phone: invite.phone || "", text: msg, context: "nps" });
    onClose?.();
  };
  const copy = () => {
    if (!invite) return;
    navigator.clipboard.writeText(invite.url);
    toast.success("Link copiado!");
  };
  return (
    <Dialog open={!!invite} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-md" data-testid="nps-invite-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            ⭐ Pesquisa de satisfação
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-sm text-muted-foreground">
            Atendimento de <strong>{invite?.name}</strong> concluído com sucesso.
            Envie a pesquisa agora — ele volta em segundos.
          </div>
          <code className="block px-2 py-1.5 rounded bg-muted text-[11px] font-mono truncate">
            {invite?.url}
          </code>
        </div>
        <DialogFooter className="gap-2 flex-col sm:flex-row sm:flex-wrap sm:justify-end">
          <Button variant="ghost" onClick={onClose} data-testid="nps-invite-skip" className="w-full sm:w-auto order-3 sm:order-none">
            Não enviar agora
          </Button>
          <Button variant="outline" onClick={copy} data-testid="nps-invite-copy" className="w-full sm:w-auto order-2 sm:order-none">
            Copiar link
          </Button>
          <Button
            className="gap-2 w-full sm:w-auto order-1 sm:order-none" onClick={send} data-testid="nps-invite-send"
            style={{ background: `hsl(var(--tenant-primary))`, color: `hsl(var(--tenant-primary-foreground))` }}
          >
            <MessageCircle className="w-4 h-4" /> Enviar via WhatsApp
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
